import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { BankLog, Setting, VatInvoice } from "lib/models";
import toast from "react-hot-toast";
import { apiGet, apiPost } from "components/Common/Axios";
import { Dialog, DialogContent } from "components/ui/dialog";
import BankLogList, { ListType } from "./BankLogList";
import { useAlert } from "components/ui/alert-context";

const BankLogPopupSelect = ({
  open,
  onOpenChange,
  vatInvoice,
  reload,
  setting,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  vatInvoice?: VatInvoice;
  reload: () => void;
  setting?: Setting;
}) => {
  const [bankLogs, setBankLogs] = useState<BankLog[]>([]);
  const { show } = useAlert();

  const loadBankLogs = async () => {
    const response = await apiGet("/bank/log");
    if (response.error) {
      toast.error("통장 내역을 불러오는데 실패했습니다.");
      console.error("loadBankLogs error", response.error);
      return;
    }

    setBankLogs(response.data);
  };

  useEffect(() => {
    if (open) {
      loadBankLogs();
    }
  }, [open]);

  const onSelectComplete = async (selectedBankLogs: BankLog[]) => {
    if (selectedBankLogs.length < 1) {
      toast.error("통장 내역을 선택해주세요.");
      return;
    }

    const response = await apiPost("/bank/connect-vat-bank", {
      vatInvoiceId: vatInvoice?.id,
      bankLogIds: selectedBankLogs.map((log) => log.id),
      depositAmt: selectedBankLogs.reduce(
        (acc, log) => acc + log.depositAmt,
        0
      ),
      depositDate: selectedBankLogs[0].depositDate,
    });

    if (response.error) {
      toast.error("입금선택 실패했습니다.");
      console.error("connect-vat-bank error", response.error);
      return;
    }

    reload();
    onOpenChange(false);
  };

  const onSelectCancel = async () => {
    show("확인", "입금선택을 취소하시겠습니까?", "confirm", async () => {
      const response = await apiPost("/bank/cancel-vat-bank", {
        vatInvoiceId: vatInvoice?.id,
      });

      if (response.error) {
        toast.error("입금선택을 취소하는데 실패했습니다.");
        console.error("cancel-vat-bank error", response.error);
        return;
      }

      reload();
      onOpenChange(false);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] md:max-w-screen-xl  max-h-[80%] overflow-x-hidden overflow-y-auto thin-scrollbar pb-0">
        <Card className="border-0 shadow-none">
          <CardHeader className="px-0 pt-0 pb-3 mb-1 sm:px-2 border-b border-gray-100">
            <CardTitle className="text-xl sm:text-2xl font-bold text-center text-gray-900">
              통장 내역
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0 sm:px-2 flex flex-col gap-3 text-sm">
            <BankLogList
              bankLogs={bankLogs}
              setBankLogs={setBankLogs}
              listType={ListType.selectInput}
              vatInvoice={vatInvoice}
              onOpenChange={onOpenChange}
              onSelectComplete={onSelectComplete}
              onSelectCancel={onSelectCancel}
              setting={setting}
            />
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default BankLogPopupSelect;
